/* eslint-disable no-console */
import { useEffect, useRef } from 'react';
import { UserActivity } from '../UserActivity';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

const moduleNames = {
	news               : 'News Feed',
	collaboration      : 'Collaboration',
	resource           : 'Resources',
	certification      : 'Certification',
	tasks              : 'Tasks',
	connections        : 'Network',
	drive              : 'Media Drive',
	meetings           : 'Meetings',
	webinars           : 'Webinar',
	affiliate          : 'Affiliate Programme',
	deal               : 'Deals',
	businessprospects  : 'Business Prospects',
	productWarranty    : 'Product Warranty',
	contractManagement : 'Contracts'
};

// 'News Feed', 'Collaboration', 'Resources', 'Certification', 'Tasks', 'Network',
// 'Media Drive', 'Meetings', 'Webinar', 'Affiliate Programme', 'Deals', 'Business Prospects',
// 'Product Warranty', 'Contracts'

const useAnalyticsTrigger = () =>
{
	const entryTimeRef = useRef<null | number>(null);
	const currentHourRef = useRef(new Date().getHours());
	const location = useLocation();
	const currentPath = location.pathname.split('/')[1];
	const session = useSelector((state:any) => state?.mysession?.session);

	const handleBusinessApi = async () =>
	{
		if (moduleNames[currentPath])
		{
			const exitTime = Date.now();
			let durationInMs = exitTime - (entryTimeRef.current || 0);

			// Duration validation constants
			const MIN_DURATION = 2500; // 2.5 second minimum
			const MAX_DURATION = 3600000; // 1 hour maximum (3600000ms)

			// Check minimum duration
			if (durationInMs < MIN_DURATION)
			{
				entryTimeRef.current = null;

				return;
			}

			// Check maximum duration and cap if exceeded
			if (durationInMs > MAX_DURATION)
			{
				// Cap the duration to maximum allowed
				durationInMs = MAX_DURATION;
			}

			const body = {
				visitLogs : {
					platformType : 'web',
					platformName : 'web',
					entryTime    : entryTimeRef.current,
					exitTime,
					durationInMs,
					moduleName   : moduleNames[currentPath]
				},
				day       : new Date().getDate(),
				month     : new Date().getMonth() + 1,
				year      : new Date().getFullYear(),
				createdAt : new Date(entryTimeRef.current || new Date()).setHours(0, 0, 0, 0)
			};

			entryTimeRef.current = null;

			try
			{
				await UserActivity(body);
			}
			catch (error)
			{
				console.error('[Analytics] Failed to log activity:', {
					module : moduleNames[currentPath],
					error  : error instanceof Error ? error.message : error
				});
			}
		}
	};

	const updateEntryTime = () =>
	{
		entryTimeRef.current = Date.now();
	};

	const handleVisibilityChange = () =>
	{
		if (document.visibilityState === 'hidden')
		{
			// console.log('** tab is now hidden for \n', currentPath, '\n', new Date());
			handleBusinessApi();
		}
		else if (document.visibilityState === 'visible')
		{
			updateEntryTime();
		}
	};

	useEffect(() =>
	{
		if (session?.role !== 'individual' && Object?.keys(session ?? {})?.length > 0)
		{
			updateEntryTime();

			return () =>
			{
			// console.log('**tab is now changed from \n', currentPath, '\n', new Date());
				handleBusinessApi();
			};
		}
	}, [ currentPath ]);

	useEffect(() =>
	{
		if (session?.role !== 'individual')
		{
			entryTimeRef.current = Date.now();

			document.addEventListener('visibilitychange', handleVisibilityChange);

			return () =>
			{
				document.removeEventListener('visibilitychange', handleVisibilityChange);
			};
		}
	}, []);

	const checkInterval = async () =>
	{
		const newHour = new Date().getHours();

		if (newHour !== currentHourRef.current && document.visibilityState === 'visible')
		{
			currentHourRef.current = newHour;
			await handleBusinessApi();

			entryTimeRef.current = Date.now();
		}
	};

	useEffect(() =>
	{
		// Check for hour change every minute
		if (session?.role !== 'individual')
		{
			const interval = setInterval(checkInterval, 60000); // Check every minute

			return () =>
			{
				clearInterval(interval);
			};
		}
	}, [ ]);
};

export default useAnalyticsTrigger;